<script lang="ts">
  import { onMount } from 'svelte';
  import {
    fetchProductionCore,
    fetchProductions,
    type ProductionCorePayload
  } from '../lib/production/api';
  import { formatDayLabel, formatDeadline, formatLocalDate, statusLabel } from '../lib/production/format';
  import { getSelectedProductionId, setSelectedProductionId } from '../lib/production/selection';

  let data: ProductionCorePayload | null = null;
  let loading = true;
  let error = false;
  let stale = false;

  onMount(() => {
    const handleOnline = () => void load();
    window.addEventListener('online', handleOnline);
    void load();
    return () => window.removeEventListener('online', handleOnline);
  });

  async function load() {
    try {
      const productions = await fetchProductions();
      const remembered = await getSelectedProductionId();
      const active =
        productions.find((production) => production.id === remembered) ??
        productions.find((production) => production.status === 'IN_PRODUCTION') ??
        productions[0];
      if (!active) {
        data = null;
        error = false;
        return;
      }
      const result = await fetchProductionCore(active.id);
      data = result.data;
      await setSelectedProductionId(active.id);
      stale = result.stale;
      error = false;
    } catch {
      error = true;
    } finally {
      loading = false;
    }
  }

  function deliverablesForDay(dayId: string) {
    return data?.deliverables.filter((deliverable) => deliverable.productionDayId === dayId) ?? [];
  }

  function runsheetCount(dayId: string) {
    return data?.runsheet.filter((item) => item.productionDayId === dayId).length ?? 0;
  }
</script>

{#if loading}
  <section class="production-view"><p>Carico produzione…</p></section>
{:else if error}
  <section class="production-view empty">
    <span class="eyebrow">PRODUZIONE</span>
    <h1>Dati non disponibili</h1>
    <button type="button" onclick={() => void load()}>Riprova</button>
  </section>
{:else if !data}
  <section class="production-view empty">
    <span class="eyebrow">PRODUZIONE</span>
    <h1>Nessuna produzione</h1>
    <p>Esegui il seed Sponz o crea una produzione quando il relativo editor sarà disponibile.</p>
  </section>
{:else}
  <section class="production-view" data-testid="production-view">
    <header class="production-master">
      <div>
        <div class="heading-meta">
          <span class="eyebrow">{data.production.status.replaceAll('_', ' ')}</span>
          {#if stale}<span class="offline-copy">COPIA OFFLINE</span>{/if}
        </div>
        <h1>{data.production.title}</h1>
        <p>{data.production.client} · {data.production.locationSummary}</p>
      </div>
      <dl>
        <div><dt>DATE</dt><dd>{formatLocalDate(data.production.startDate)} — {formatLocalDate(data.production.endDate)}</dd></div>
        <div><dt>BASE</dt><dd>{data.production.accommodationBase ?? '—'}</dd></div>
        <div><dt>TIMEZONE</dt><dd>{data.production.timezone}</dd></div>
      </dl>
    </header>

    <div class="production-grid">
      <section class="section-block days-section" aria-labelledby="days-title">
        <div class="section-heading"><h2 id="days-title">GIORNATE</h2><span>{data.days.length}</span></div>
        <div class="day-list">
          {#each data.days as day (day.id)}
            <article class="day-row">
              <div class="day-id">
                <strong>{formatDayLabel(day.dayNumber)}</strong>
                <span>{formatLocalDate(day.date)}</span>
              </div>
              <div class="day-times">
                <span>CALL <strong>{day.crewCall ?? 'TBD'}</strong></span>
                <span>FIRST <strong>{day.firstShot ?? 'TBD'}</strong></span>
              </div>
              <div class="day-state">
                <span>{runsheetCount(day.id)} runsheet</span>
                <strong>{deliverablesForDay(day.id).length} delivery</strong>
              </div>
            </article>
          {/each}
        </div>
        {#if data.runsheet.length === 0}
          <p class="source-note">Runsheet, eventi, KOL e priorità restano volutamente vuoti finché la produzione non conferma la SSOT operativa.</p>
        {/if}
      </section>

      <section class="section-block" aria-labelledby="crew-title">
        <div class="section-heading"><h2 id="crew-title">CREW</h2><span>{data.crew.length}</span></div>
        <div class="crew-list">
          {#each data.crew as member (member.id)}
            <article>
              <span class="crew-label">{member.label ?? 'CREW'}</span>
              <div>
                <strong>{member.name}</strong>
                <p>{member.productionRole}</p>
              </div>
            </article>
          {/each}
        </div>
      </section>

      <section class="section-block" aria-labelledby="contacts-title">
        <div class="section-heading"><h2 id="contacts-title">CONTATTI</h2><span>{data.contacts.length}</span></div>
        {#if data.contacts.length > 0}
          <div class="contact-list">
            {#each data.contacts as contact (contact.id)}
              <article>
                <div><strong>{contact.displayName}</strong><span>{contact.role ?? contact.category}</span></div>
                <div class="contact-actions">
                  {#if contact.phone}<a href={`tel:${contact.phone}`}>Chiama</a>{/if}
                  {#if contact.email}<a href={`mailto:${contact.email}`}>Email</a>{/if}
                </div>
              </article>
            {/each}
          </div>
        {:else}
          <div class="compact-empty">
            <strong>Nessun contatto confermato nel seed.</strong>
            <span>I riferimenti non vengono inventati: saranno acquisiti dalla documentazione operativa.</span>
          </div>
        {/if}
      </section>

      <section class="section-block delivery-section" aria-labelledby="deliverables-title">
        <div class="section-heading"><h2 id="deliverables-title">DELIVERABLE</h2><span>{data.deliverables.length}</span></div>
        <div class="deliverable-list">
          {#each data.deliverables as deliverable (deliverable.id)}
            <article>
              <div>
                <strong>{deliverable.type}</strong>
                <span>{deliverable.title}</span>
                <small>{deliverable.deadline ? `${formatDeadline(deliverable.deadline).date} · ${formatDeadline(deliverable.deadline).time}` : 'DEADLINE TBD'}</small>
              </div>
              <span class="deliverable-state" data-status={deliverable.status}>{statusLabel(deliverable.status)}</span>
            </article>
          {/each}
        </div>
      </section>
    </div>
  </section>
{/if}

<style>
  .production-view {
    width: min(100%, var(--content-max));
    margin: 0 auto;
    padding: var(--space-6) 0 var(--space-10);
  }

  .production-view.empty h1 {
    margin-bottom: var(--space-2);
  }

  .production-view.empty button {
    min-height: 44px;
    border: 1px solid var(--border-strong);
    background: var(--surface);
    border-radius: var(--radius-sm);
    padding: 0 var(--space-4);
  }

  .production-master {
    padding-bottom: var(--space-6);
    border-bottom: 1px solid var(--border-strong);
  }

  .heading-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
  }

  .offline-copy {
    color: var(--warning);
    font-size: 0.65rem;
    font-weight: 760;
    letter-spacing: 0.08em;
  }

  .production-master h1 {
    margin: var(--space-2) 0;
    font-size: clamp(1.9rem, 8vw, 3.4rem);
    line-height: 0.98;
    letter-spacing: -0.045em;
  }

  .production-master > div > p {
    margin: 0;
    color: var(--text-secondary);
  }

  dl {
    display: grid;
    gap: var(--space-3);
    margin: var(--space-6) 0 0;
  }

  dl div {
    display: grid;
    grid-template-columns: 82px minmax(0, 1fr);
    gap: var(--space-3);
  }

  dt {
    color: var(--text-secondary);
    font-size: 0.65rem;
    font-weight: 760;
    letter-spacing: 0.08em;
  }

  dd {
    margin: 0;
    font-size: var(--type-sm);
    font-weight: 630;
  }

  .production-grid {
    display: grid;
    gap: var(--space-8);
    padding-top: var(--space-8);
  }

  .section-block {
    min-width: 0;
    border-top: 2px solid var(--text-primary);
    padding-top: var(--space-3);
  }

  .section-heading {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: var(--space-3);
  }

  .section-heading h2 {
    margin: 0;
    font-size: var(--type-sm);
    letter-spacing: 0.08em;
  }

  .section-heading span {
    color: var(--text-secondary);
    font-size: var(--type-xs);
  }

  .day-list,
  .crew-list,
  .contact-list,
  .deliverable-list {
    border-bottom: 1px solid var(--border);
  }

  .day-row {
    min-height: 76px;
    display: grid;
    grid-template-columns: 82px minmax(0, 1fr);
    gap: var(--space-3);
    padding: var(--space-3) 0;
    border-top: 1px solid var(--border);
  }

  .day-row:first-child,
  .crew-list article:first-child,
  .deliverable-list article:first-child {
    border-top: 0;
  }

  .day-id {
    display: grid;
    align-content: start;
    gap: 2px;
  }

  .day-id strong {
    font-size: var(--type-sm);
  }

  .day-id span,
  .day-times span,
  .day-state span,
  .day-state strong {
    font-size: var(--type-xs);
  }

  .day-id span,
  .day-state span {
    color: var(--text-secondary);
  }

  .day-times {
    display: flex;
    align-items: start;
    gap: var(--space-4);
  }

  .day-times span {
    display: grid;
    gap: 2px;
    color: var(--text-secondary);
    font-weight: 680;
    letter-spacing: 0.04em;
  }

  .day-times strong {
    color: var(--text-primary);
    font-size: var(--type-md);
    letter-spacing: 0;
  }

  .day-state {
    grid-column: 2;
    display: flex;
    gap: var(--space-3);
  }

  .source-note {
    margin: var(--space-3) 0 0;
    color: var(--text-secondary);
    font-size: var(--type-xs);
    line-height: 1.45;
  }

  .crew-list article {
    min-height: 70px;
    display: grid;
    grid-template-columns: 56px minmax(0, 1fr);
    gap: var(--space-3);
    align-items: start;
    padding: var(--space-3) 0;
    border-top: 1px solid var(--border);
  }

  .crew-label {
    color: var(--accent);
    font-size: var(--type-xs);
    font-weight: 780;
    letter-spacing: 0.07em;
  }

  .crew-list strong {
    font-size: var(--type-sm);
  }

  .crew-list p {
    margin: var(--space-1) 0 0;
    color: var(--text-secondary);
    font-size: var(--type-xs);
    line-height: 1.35;
  }

  .compact-empty {
    display: grid;
    gap: var(--space-1);
    padding: var(--space-3) 0;
    border-bottom: 1px solid var(--border);
  }

  .compact-empty strong,
  .compact-empty span {
    font-size: var(--type-sm);
  }

  .compact-empty span {
    color: var(--text-secondary);
    line-height: 1.4;
  }

  .contact-list article,
  .deliverable-list article {
    min-height: 60px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
    border-top: 1px solid var(--border);
  }

  .contact-list article > div:first-child,
  .deliverable-list article > div:first-child {
    min-width: 0;
    display: grid;
    gap: 2px;
  }

  .contact-list strong,
  .deliverable-list strong {
    font-size: var(--type-sm);
  }

  .contact-list span,
  .deliverable-list div span {
    overflow: hidden;
    color: var(--text-secondary);
    font-size: var(--type-xs);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .contact-actions {
    display: flex;
    gap: var(--space-2);
  }

  .contact-actions a {
    color: var(--accent);
    font-size: var(--type-xs);
    font-weight: 700;
  }

  .deliverable-list article small {
    display: block;
    margin-top: 3px;
    color: var(--text-secondary);
    font-size: 0.66rem;
    font-weight: 650;
    letter-spacing: 0.05em;
  }

  .deliverable-state {
    flex: none;
    font-size: 0.65rem;
    font-weight: 760;
    letter-spacing: 0.05em;
  }

  .deliverable-state[data-status='DELIVERED'],
  .deliverable-state[data-status='APPROVED'] {
    color: var(--success);
  }

  @media (min-width: 700px) {
    .production-master {
      display: grid;
      grid-template-columns: minmax(0, 1.3fr) minmax(300px, 0.7fr);
      gap: var(--space-8);
      align-items: end;
    }

    .production-master dl {
      margin-top: 0;
    }

    .production-grid {
      grid-template-columns: minmax(0, 1.35fr) minmax(270px, 0.65fr);
    }

    .days-section,
    .delivery-section {
      grid-column: 1;
    }
  }

  @media (min-width: 1000px) {
    .production-view {
      padding-top: var(--space-8);
    }

    .day-row {
      grid-template-columns: 100px minmax(220px, 1fr) auto;
      align-items: center;
    }

    .day-state {
      grid-column: auto;
    }
  }
</style>
